import Loader from 'reusable/mobx-helpers/Loader'
import { makeAutoObservable } from 'mobx'
import { apiClient } from 'api/trpc/trpc-client'
import { FormHandlerConfig, formHandler } from 'reusable/mobx-store-utils/form-handlers/new/form-handler'
import { snackbarError, snackbarSuccess } from 'reusable/mui/snackbar/snackbar'
import { CreateClientModalParams, TextFieldIcon } from './CreateClientModal'
import OrgGuardStore from 'features/main/main-router/org-guard/OrgGuardStore'
import variables from 'variables/variables'
import { canonicalStage } from '@gap/utils/stage'
import { AdvisorClientDto } from '@gap/backend/app-services/advisor/client/models/AdvisorClientDto'
import onEnterProp from 'reusable/utils/on-enter-prop'
import Person from '@mui/icons-material/Person'
import StorefrontOutlined from '@mui/icons-material/StorefrontOutlined'
import EmailOutlined from '@mui/icons-material/EmailOutlined'
import { PhoneOutlined } from '@mui/icons-material'
import { TextFieldProps } from '@mui/material/TextField'
import { createStoreContext } from 'reusable/mobx-store-utils/storeUtils'
import CacheStore from 'cache/CacheStore'
import { OrgSubscriptionTierStore } from 'features/main/main-router/org-guard/main-container/org-subscription-tiers/OrgSubscriptionTierStore'
import XpmConnectionStore from 'features/gap-components/xero-xpm-connect/XpmConnectionStore'

interface CreateClientModalStoreProps extends CreateClientModalParams {
   orgGuardStore: OrgGuardStore
   orgSubscriptionTierStore: OrgSubscriptionTierStore
   cacheStore: CacheStore
   xpmConnectionStore: XpmConnectionStore
   clients: AdvisorClientDto.idWithName[]
}

export default class CreateClientModalStore {
   loader = new Loader(false)
   sp
   fields: AdvisorClientDto.create = {
      name: '',
      primaryContactFirstName: '',
      primaryContactLastName: '',
      email: '',
      primaryContactPhoneNumber: '',
      accessUserIds: [],
   }
   /** Guards against re-triggering the background sync on every effect re-run within the same open. */
   private backgroundSyncTriggered = false

   constructor(sp: CreateClientModalStoreProps) {
      makeAutoObservable(this)
      this.sp = sp
      this.sp.modalState.setOnClose(() => {
         this.fields.name = ''
         this.fields.primaryContactFirstName = ''
         this.fields.primaryContactLastName = ''
         this.fields.email = ''
         this.fields.primaryContactPhoneNumber = ''
         this.fields.accessUserIds = []
         this.resetBackgroundSyncFlag()
      })
   }

   /** True while any XPM client sync is in flight, wherever it was started from. */
   get isSyncing() {
      return this.sp.xpmConnectionStore.isSyncing
   }

   get formHandler() {
      return formHandler(this.fields, {
         name: { type: 'string' },
         primaryContactFirstName: { type: 'string', onValueChange: this.setEmail, },
         primaryContactLastName: { type: 'string', onValueChange: this.setEmail },
         email: { type: 'string' },
         primaryContactPhoneNumber: { type: 'string' },
         accessUserIds: { type: 'any' },
      } satisfies FormHandlerConfig)
   }

   get visibility(): 'public' | 'private' {
      return (this.fields.accessUserIds?.length ?? 0) > 0 ? 'private' : 'public'
   }

   get advisorOrgName(): string {
      return this.sp.orgGuardStore.org?.name ?? ''
   }

   get isOnTrial(): boolean {
      return this.sp.orgGuardStore.isOnTrial
   }

   /** Whether this org can use client visibility (Chargebee entitlement, or legacy tier rule). */
   get canUseClientVisibility(): boolean {
      return this.sp.orgGuardStore.canUseClientVisibility
   }

   /** Visibility options are locked when the org can't use the feature (and isn't previewing it on trial). */
   get visibilityDisabled(): boolean {
      return !this.canUseClientVisibility
   }

   get visibilityIsPremiumFeature(): boolean {
      return !this.canUseClientVisibility || this.isOnTrial
   }

   get visibilityTooltip(): string {
      if (this.isOnTrial) {
         return 'Included in trial. Only available on select plans'
      }
      return "Your current plan doesn't include this feature. Upgrade your plan to use it."
   }

   get fieldProps() {
      return Object.keys(this.fields).reduce((acc, key) => {
         acc[key as keyof AdvisorClientDto.create] = {
            onKeyDown: onEnterProp(this.onCreateClient).onKeyDown,
            fullWidth: true,
            slotProps: {
               input: {
                  startAdornment: (() => {
                     switch (key) {
                        case 'name':
                           return TextFieldIcon(StorefrontOutlined)
                        case 'primaryContactFirstName':
                           return TextFieldIcon(Person)
                        case 'primaryContactLastName':
                           return undefined
                        case 'email':
                           return TextFieldIcon(EmailOutlined)
                        case 'primaryContactPhoneNumber':
                           return TextFieldIcon(PhoneOutlined)
                        default:
                           return undefined
                     }
                  })()
               }
            }
         }
         return acc
      }, {} as Record<keyof AdvisorClientDto.create, TextFieldProps>)
   }

   setVisibilityHandler = (event: React.MouseEvent<HTMLElement>) => {
      const value = event.currentTarget.getAttribute('data-visibility')
      if (value === 'public') {
         this.fields.accessUserIds = []
      } else if (value === 'private') {
         this.fields.accessUserIds = [this.sp.orgGuardStore.userData.id]
      }
   }

   viewPlansHandler = () => {
      this.sp.orgSubscriptionTierStore.upgradePlanModal.open()
   }

   /**
    * util method to auto-generate email for dev & test environments
    */
   setEmail = () => {
      if (!variables.useQuickFill || ['prod', 'preprod'].includes(canonicalStage(variables.stage) ?? '')) {
         return
      }

      if (!this.fields.primaryContactFirstName && !this.fields.primaryContactLastName) {
         this.fields.email = ''
         return
      }
      // get logged in user's first name
      const gapUserFirstName = this.sp.orgGuardStore.userData.email.split('+')[0]
      const lastNamePart = this.fields.primaryContactLastName ? `-${this.fields.primaryContactLastName}` : ''
      this.fields.email = `${gapUserFirstName}+${this.fields.primaryContactFirstName}${lastNamePart}@${variables.gapDomain}`.toLowerCase()
   }

   onCreateClient = async () => {
      await this.loader.executeLoading(async () => {
         if (this.sp.clients.find(client => client.name.toLowerCase() === this.fields.name.toLowerCase())) {
            return snackbarError('A client with this name already exists. Please try again with a different client name or use the existing client.')
         }

         if (this.formHandler.validate(AdvisorClientDto.createSchema)) {
            const fields = JSON.parse(JSON.stringify(this.fields))
            const client = await apiClient.advisor.addClientManually.mutate(fields)
            this.sp.modalState.close()

            snackbarSuccess('Client created')
            // Add client to cache
            setTimeout(() => {
               // A websocket event should auto add the client. If it failed, manually add it here
               if (!this.sp.clients.find(c => c.id === client.id)) {
               this.sp.clients.push({
                     id: client.id,
                     name: client.name,
                     clientOrgId: client.clientOrgId,
                  })
               }
            }, 200)

            if (this.fields.email === this.sp.orgGuardStore.userData.email) {
               const userInfo = await apiClient.user.getMyUserInfo.query()
               this.sp.orgGuardStore.setUserInfo(userInfo)
            }

            this.sp.onCreateClientCallback(client)
         }
      })
   }

   /**
    * When the modal opens on the "Add manually" tab and XPM is connected, kick off a
    * background sync so the client the user is about to enter may already arrive from XPM
    * — refreshing the autocomplete cache if the sync completes before they submit.
    */
   triggerBackgroundXpmSyncIfConnected = async () => {
      if (this.backgroundSyncTriggered) return
      this.backgroundSyncTriggered = true
      await this.sp.xpmConnectionStore.runSync({ ifConnected: true })
   }

   resetBackgroundSyncFlag = () => {
      this.backgroundSyncTriggered = false
   }
}

export const { Provider: CreateClientModalStoreProvider, useProvidedStore: useCreateClientModalStore } = createStoreContext(CreateClientModalStore)
